/**
 * KRA Photo Journal – Thêm bộ ảnh vào hệ thống
 *
 * Admin-only panel for registering existing Drive folders as Airtable records
 * WITHOUT moving them. Counterpart to the "Sắp xếp" panel which moves folders
 * during the same operation.
 *
 * Flow:
 *   1. Input — admin pastes one or more Drive folder links (one per line)
 *   2. Resolve — for each link the panel fetches folder + parent metadata via
 *      OAuth, then auto-suggests:
 *        - kimono_tag from the parent folder name, else the set's own name
 *        - shoot/season/scene/color tags from the folder name itself
 *        (both via `suggestFolderTags`)
 *   3. Review — admin can rename, toggle suggested tags, open in Drive,
 *      remove items, then bulk-import
 *   4. Import — POST /api/bulk-import (no `notifyAs:"sort"` since folders
 *      stay in place)
 *
 * Auto-tag patterns live in `@kra/photo-journal-shared/auto-tag` so the
 * worker (Bulk Sort flow) and this panel agree on a single source of truth.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { suggestFolderTags } from "@kra/photo-journal-shared";
import {
  bulkImport,
  type ImportFolder,
} from "../lib/api.js";
import {
  useGoogleDrive,
  extractFolderIdFromUrl,
  firstImageThumbnail,
  folderUrl,
  getFolderWithParentName,
  listSubfolders,
  getFolder,
  type DriveFolder,
} from "../hooks/useGoogleDrive.js";

/** localStorage key shared with ImportToDriveModal so the destination picker
 *  remembers the last folder the admin imported into. */
const LAST_DEST_KEY = "kra_last_drive_folder";

interface BulkImportPanelProps {
  onClose: () => void;
}

type Phase = "input" | "resolving" | "review" | "importing" | "done";

/** A photo-set folder after resolution. One per pasted link. */
interface ResolvedItem {
  /** Drive folder ID */
  id: string;
  /** Original folder name from Drive (read-only) */
  driveName: string;
  /** Editable Airtable record name (defaults to driveName) */
  recordName: string;
  /** Parent folder name on Drive (informs kimono_tag suggestion) */
  parentName: string | null;
  /** Suggested tags. User can toggle each chip off in review. */
  kimonoTags: string[];
  shootTags: string[];
  seasonTags: string[];
  sceneTags: string[];
  colorTags: string[];
  /** Tags the user has explicitly disabled (kept separate so the chip can
   *  still render so they can toggle it back on). */
  disabledTags: Set<string>;
  /** Preview thumbnail (lazy-loaded, may be null) */
  thumbnail: string | null;
  thumbLoaded: boolean;
  /** Resolution failure (e.g. invalid link, no permission) */
  error?: string;
  /** Whether this item is included in the import */
  selected: boolean;
}

/** Parse the textarea into one folder ID per non-empty line. Strips trailing
 *  query strings. Lines that aren't recognisable as Drive folder URLs/IDs
 *  are returned with `id=null` so we can show them as errors in the review. */
function parseLinks(raw: string): Array<{ raw: string; id: string | null }> {
  const out: Array<{ raw: string; id: string | null }> = [];
  const seenIds = new Set<string>();
  const seenRaw = new Set<string>();
  for (const line of raw.split(/[\r\n,]+/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    let id = extractFolderIdFromUrl(trimmed);
    if (!id && /^[a-zA-Z0-9_-]{20,}$/.test(trimmed)) id = trimmed;
    if (id) {
      if (seenIds.has(id)) continue;
      seenIds.add(id);
    } else {
      // Dedupe invalid lines too so we don't crash React with duplicate keys
      if (seenRaw.has(trimmed)) continue;
      seenRaw.add(trimmed);
    }
    out.push({ raw: trimmed, id });
  }
  return out;
}

export function BulkImportPanel({ onClose }: BulkImportPanelProps) {
  const { accessToken, requestToken } = useGoogleDrive();

  const [phase, setPhase] = useState<Phase>("input");
  const [linksText, setLinksText] = useState("");
  const [items, setItems] = useState<ResolvedItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<{
    created: number;
    errors: Array<{ folderName: string; error: string }>;
  } | null>(null);

  // Copy-to-my-Drive: when on, each registered folder is also copied into the
  // admin's own Drive (for third-party photographer folders that could be
  // deleted). Requires a destination folder + a Google token.
  const [copyToMyDrive, setCopyToMyDrive] = useState(false);
  const [destFolderId, setDestFolderId] = useState<string | null>(null);
  const [destFolderName, setDestFolderName] = useState<string>("");

  const ensureToken = useCallback(async (): Promise<string | null> => {
    if (accessToken) return accessToken;
    try {
      return await requestToken();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Đăng nhập Google thất bại");
      return null;
    }
  }, [accessToken, requestToken]);

  const toggleCopy = useCallback(
    async (on: boolean) => {
      setCopyToMyDrive(on);
      if (on && !accessToken) await ensureToken();
    },
    [accessToken, ensureToken],
  );

  const updateItem = useCallback((id: string, patch: Partial<ResolvedItem>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  // ── Resolve ───────────────────────────────────────────────────────────────

  const handleResolve = useCallback(async () => {
    const parsed = parseLinks(linksText);
    if (parsed.length === 0) {
      setError("Paste ít nhất 1 link folder Google Drive.");
      return;
    }

    setError(null);
    const token = await ensureToken();
    if (!token) return;

    setPhase("resolving");

    // Resolve in parallel — Drive's per-file metadata endpoint is cheap
    const results = await Promise.all(
      parsed.map(async ({ raw, id }): Promise<ResolvedItem> => {
        if (!id) {
          return {
            id: `invalid:${raw}`,
            driveName: raw,
            recordName: raw,
            parentName: null,
            kimonoTags: [],
            shootTags: [],
            seasonTags: [],
            sceneTags: [],
            colorTags: [],
            disabledTags: new Set(),
            thumbnail: null,
            thumbLoaded: true,
            error: "Link không hợp lệ",
            selected: false,
          };
        }
        try {
          const meta = await getFolderWithParentName(id, token);
          return {
            id: meta.id,
            driveName: meta.name,
            recordName: meta.name,
            parentName: meta.parentName,
            ...suggestFolderTags(meta.name, meta.parentName),
            disabledTags: new Set(),
            thumbnail: null,
            thumbLoaded: false,
            selected: true,
          };
        } catch (e) {
          return {
            id,
            driveName: raw,
            recordName: raw,
            parentName: null,
            kimonoTags: [],
            shootTags: [],
            seasonTags: [],
            sceneTags: [],
            colorTags: [],
            disabledTags: new Set(),
            thumbnail: null,
            thumbLoaded: true,
            error: e instanceof Error ? e.message : "Không lấy được metadata",
            selected: false,
          };
        }
      }),
    );

    setItems(results);
    setPhase("review");

    // Fire-and-forget thumbnail fetches
    for (const it of results) {
      if (it.error) continue;
      firstImageThumbnail(it.id, token)
        .then((thumb) => updateItem(it.id, { thumbnail: thumb, thumbLoaded: true }))
        .catch(() => updateItem(it.id, { thumbLoaded: true }));
    }
  }, [linksText, ensureToken, updateItem]);

  // ── Import ────────────────────────────────────────────────────────────────

  const selectedCount = useMemo(
    () => items.filter((it) => it.selected && !it.error).length,
    [items],
  );

  const handleImport = useCallback(async () => {
    const folders: ImportFolder[] = [];
    for (const it of items) {
      if (!it.selected || it.error) continue;
      const useTag = (tag: string) => !it.disabledTags.has(tag);
      folders.push({
        folderId: it.id,
        folderName: it.recordName.trim() || it.driveName,
        kimonoTags: it.kimonoTags.filter(useTag),
        shootTags: it.shootTags.filter(useTag),
        seasonTags: it.seasonTags.filter(useTag),
        sceneTags: it.sceneTags.filter(useTag),
        colorTags: it.colorTags.filter(useTag),
        // Only meaningful when copying into the admin's Drive — that dest
        // folder IS the category (e.g. "Couples"). Skipped for register-only.
        categoryFolder: copyToMyDrive && destFolderName ? destFolderName : undefined,
      });
    }
    if (folders.length === 0) return;

    if (copyToMyDrive && !destFolderId) {
      setError("Hãy chọn folder đích trong Drive của bạn để copy ảnh vào.");
      return;
    }

    setPhase("importing");
    setError(null);
    try {
      const result = await bulkImport(
        folders,
        copyToMyDrive && destFolderId && accessToken
          ? { copyToMyDrive: true, destFolderId, oauthToken: accessToken }
          : undefined,
      );
      setImportResult(result);
      setPhase("done");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Import failed");
      setPhase("review");
    }
  }, [items, copyToMyDrive, destFolderId, destFolderName, accessToken]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <motion.div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="bg-[#1A1917] rounded-2xl w-full max-w-4xl max-h-[88vh] overflow-hidden flex flex-col"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/10">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-white">
                📦 Thêm bộ ảnh vào hệ thống
              </h2>
              <p className="text-xs text-white/50 mt-1 leading-relaxed">
                Paste link folder Drive của 1 hoặc nhiều bộ ảnh (1 link / dòng).
                Hệ thống tạo record Airtable cho từng bộ — folder Drive
                <strong className="text-white/70"> không bị di chuyển</strong>.
                Dùng cho ảnh đã được sắp xếp đúng chỗ rồi, chỉ cần đăng ký vào hệ thống.
                <br />
                <span className="text-white/35">
                  Cần dọn ảnh mới tải xuống vào đúng nhóm? Dùng <strong>Sắp xếp</strong> bên cạnh.
                </span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/40 hover:text-white/80 text-2xl leading-none flex-shrink-0"
            >
              ×
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="bg-red-500/20 text-red-300 px-4 py-3 rounded-lg text-sm"
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Phase: Input */}
          {phase === "input" && (
            <div className="space-y-3">
              <p className="text-white/60 text-sm">
                Mỗi dòng là 1 link folder Drive (hoặc Folder ID). Hệ thống sẽ
                tự suggest kimono tag dựa trên tên folder cha.
              </p>
              <textarea
                value={linksText}
                onChange={(e) => setLinksText(e.target.value)}
                rows={10}
                placeholder={`https://drive.google.com/drive/folders/abc123...\nhttps://drive.google.com/drive/folders/xyz789...\nhttps://drive.google.com/drive/folders/...`}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#B76E6E]/50 font-mono resize-y"
              />
              <button
                onClick={handleResolve}
                disabled={!linksText.trim()}
                className="w-full bg-[#B76E6E] hover:bg-[#a05e5e] disabled:opacity-30 text-white font-medium py-3 rounded-xl transition-colors"
              >
                Phân tích link
              </button>
            </div>
          )}

          {/* Phase: Resolving */}
          {phase === "resolving" && (
            <div className="flex flex-col items-center justify-center py-12 space-y-3">
              <div className="w-8 h-8 border-2 border-[#B76E6E] border-t-transparent rounded-full animate-spin" />
              <p className="text-white/50 text-sm">Đang phân tích link…</p>
            </div>
          )}

          {/* Phase: Review */}
          {phase === "review" && items.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-white/60 text-sm">
                  <span className="text-white font-medium">{items.length}</span> link ·
                  Sẽ import <span className="text-[#B76E6E] font-medium">{selectedCount}</span>
                  {items.some((it) => it.error) && (
                    <span className="text-red-400/80">
                      {" · "}{items.filter((it) => it.error).length} lỗi
                    </span>
                  )}
                </p>
                <button
                  onClick={() => { setPhase("input"); setItems([]); }}
                  className="text-white/40 hover:text-white/70 text-xs"
                >
                  ← Sửa link
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((it) => (
                  <ResolvedCard
                    key={it.id}
                    item={it}
                    onChange={(patch) => updateItem(it.id, patch)}
                    onRemove={() => removeItem(it.id)}
                    onToggleTag={(tag) => {
                      const next = new Set(it.disabledTags);
                      if (next.has(tag)) next.delete(tag);
                      else next.add(tag);
                      updateItem(it.id, { disabledTags: next });
                    }}
                  />
                ))}
              </div>

              {/* Copy-to-my-Drive (third-party photographer folders) */}
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 space-y-3">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={copyToMyDrive}
                    onChange={(e) => void toggleCopy(e.target.checked)}
                    className="accent-[#B76E6E] w-4 h-4 mt-0.5 flex-shrink-0"
                  />
                  <span className="text-sm text-white/85">
                    Ảnh của thợ khác —{" "}
                    <span className="text-[#E5B0B0]">copy về Drive của tôi</span>
                    <span className="block text-[11px] text-white/40 mt-0.5 leading-relaxed">
                      Ảnh được copy vào folder bạn chọn (chạy nền, báo Discord khi
                      xong). Record tự trỏ sang bản sao của bạn — thợ xoá bản gốc
                      cũng không mất. Lưu ý: các subfolder (Compressed / Full Size)
                      sẽ được gộp phẳng vào 1 folder.
                    </span>
                  </span>
                </label>

                {copyToMyDrive && accessToken && (
                  <DestFolderPicker
                    token={accessToken}
                    selectedId={destFolderId}
                    onPick={(id, name) => {
                      setDestFolderId(id);
                      setDestFolderName(name);
                    }}
                  />
                )}
                {copyToMyDrive && !accessToken && (
                  <p className="text-[12px] text-white/40">Đang đăng nhập Google…</p>
                )}
                {copyToMyDrive && destFolderId && (
                  <p className="text-[12px] text-emerald-300/90">
                    ✓ Sẽ copy vào: <strong>{destFolderName}</strong>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Phase: Importing */}
          {phase === "importing" && (
            <div className="flex flex-col items-center justify-center py-12 space-y-3">
              <div className="w-8 h-8 border-2 border-[#B76E6E] border-t-transparent rounded-full animate-spin" />
              <p className="text-white/50 text-sm">Đang import {selectedCount} bộ ảnh…</p>
            </div>
          )}

          {/* Phase: Done */}
          {phase === "done" && importResult && (
            <div className="space-y-4 text-center py-8">
              <div className="text-4xl">✅</div>
              <p className="text-white text-lg font-medium">Import hoàn tất!</p>
              <p className="text-white/60 text-sm">
                Đã tạo <span className="text-[#B76E6E] font-medium">{importResult.created}</span> records.
                {importResult.errors.length > 0 && (
                  <span className="text-red-400">
                    {" "}{importResult.errors.length} lỗi.
                  </span>
                )}
              </p>
              <p className="text-white/40 text-xs">
                {copyToMyDrive
                  ? "Ảnh đang được copy vào Drive của bạn (chạy nền). Bạn sẽ nhận thông báo Discord khi từng bộ xong; record sẽ tự trỏ sang bản sao của bạn."
                  : "Folder Drive vẫn ở yên tại vị trí cũ. Preview sẽ được tự động generate trong vài phút."}
              </p>

              {importResult.errors.length > 0 && (
                <div className="mt-4 bg-red-500/10 rounded-lg p-4 text-left">
                  <p className="text-red-400 text-xs font-medium mb-2">Lỗi:</p>
                  {importResult.errors.map((e, i) => (
                    <p key={i} className="text-red-300/70 text-xs">
                      • {e.folderName}: {e.error}
                    </p>
                  ))}
                </div>
              )}

              <button
                onClick={onClose}
                className="mt-4 bg-white/10 hover:bg-white/15 text-white px-6 py-2.5 rounded-xl transition-colors"
              >
                Đóng
              </button>
            </div>
          )}
        </div>

        {/* Footer (Review phase only) */}
        {phase === "review" && (
          <div className="px-6 py-4 border-t border-white/10">
            <button
              onClick={handleImport}
              disabled={selectedCount === 0 || (copyToMyDrive && !destFolderId)}
              className="w-full bg-[#B76E6E] hover:bg-[#a05e5e] disabled:opacity-30 text-white font-medium py-3 rounded-xl transition-colors"
            >
              {copyToMyDrive
                ? `Import + copy ${selectedCount} bộ ảnh`
                : `Import ${selectedCount} bộ ảnh`}
            </button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

// ── Resolved card ────────────────────────────────────────────────────────────

interface ResolvedCardProps {
  item: ResolvedItem;
  onChange: (patch: Partial<ResolvedItem>) => void;
  onRemove: () => void;
  onToggleTag: (tag: string) => void;
}

function ResolvedCard({ item, onChange, onRemove, onToggleTag }: ResolvedCardProps) {
  const isError = !!item.error;
  const allTags: Array<{ tag: string; group: string }> = [];
  for (const t of item.kimonoTags) allTags.push({ tag: t, group: "kimono" });
  for (const t of item.shootTags) allTags.push({ tag: t, group: "shoot" });
  for (const t of item.seasonTags) allTags.push({ tag: t, group: "season" });
  for (const t of item.sceneTags) allTags.push({ tag: t, group: "scene" });
  for (const t of item.colorTags) allTags.push({ tag: t, group: "color" });

  return (
    <div
      className={`rounded-xl border overflow-hidden flex flex-col relative bg-white/5 ${
        isError
          ? "border-red-500/40 opacity-70"
          : item.selected
            ? "border-white/15"
            : "border-white/5 opacity-50"
      }`}
    >
      {/* Top-right action overlay */}
      {!isError && (
        <div className="absolute top-2 right-2 z-10 flex items-center gap-1.5">
          <a
            href={folderUrl(item.id)}
            target="_blank"
            rel="noopener noreferrer"
            title="Mở folder trên Drive"
            aria-label="Mở folder trên Drive"
            className="flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white/70 hover:bg-blue-500/30 hover:text-white transition-colors"
          >
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          </a>
          <button
            onClick={onRemove}
            title="Bỏ khỏi danh sách"
            aria-label="Bỏ khỏi danh sách"
            className="flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white/70 hover:bg-red-500/30 hover:text-white transition-colors"
          >
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Thumbnail */}
      <div className="aspect-[4/3] bg-black/30 relative">
        {isError ? (
          <div className="h-full w-full flex flex-col items-center justify-center gap-1 px-4 text-center">
            <span className="text-2xl">⚠️</span>
            <p className="text-xs text-red-300/80 line-clamp-2">{item.error}</p>
            <p className="text-[10px] text-white/30 truncate w-full" title={item.driveName}>
              {item.driveName}
            </p>
          </div>
        ) : item.thumbnail ? (
          <img
            src={item.thumbnail}
            alt={item.driveName}
            className="h-full w-full object-cover"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-xs text-white/30">
            {item.thumbLoaded ? "Không có ảnh" : "Đang tải…"}
          </div>
        )}
      </div>

      {/* Body */}
      {!isError && (
        <div className="p-3 flex flex-col gap-2 flex-1">
          {/* Selection toggle + parent path */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={item.selected}
              onChange={(e) => onChange({ selected: e.target.checked })}
              className="accent-[#B76E6E] w-3.5 h-3.5 flex-shrink-0"
            />
            <span className="text-[10px] text-white/40 truncate" title={item.parentName ?? ""}>
              {item.parentName ? `📁 ${item.parentName}` : "📁 (root)"}
            </span>
          </div>

          {/* Editable record name */}
          <input
            type="text"
            value={item.recordName}
            onChange={(e) => onChange({ recordName: e.target.value })}
            placeholder="Tên record"
            className="w-full rounded-lg bg-white/10 border border-white/10 px-2.5 py-1.5 text-xs text-white focus:border-[#B76E6E]/50 focus:outline-none"
          />

          {/* Suggested tag chips */}
          {allTags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {allTags.map(({ tag, group }) => {
                const off = item.disabledTags.has(tag);
                const groupColor =
                  group === "kimono" ? "bg-[#B76E6E]/30 text-[#E5B0B0]" :
                  group === "shoot"  ? "bg-blue-500/20 text-blue-300" :
                  group === "season" ? "bg-pink-500/20 text-pink-300" :
                  group === "color"  ? "bg-amber-500/20 text-amber-300" :
                                       "bg-emerald-500/20 text-emerald-300";
                return (
                  <button
                    key={`${group}:${tag}`}
                    onClick={() => onToggleTag(tag)}
                    className={`text-[10px] px-1.5 py-0.5 rounded transition-opacity ${
                      off ? "opacity-30 line-through" : ""
                    } ${groupColor}`}
                    title={off ? "Bật lại tag này" : "Tắt tag này (không gắn vào record)"}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
          )}

          {allTags.length === 0 && (
            <p className="text-[10px] text-white/30 italic">
              Không có tag suggest — gắn tag thủ công sau khi import.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Destination folder picker (copy-to-my-Drive) ─────────────────────────────

interface DestFolderPickerProps {
  token: string;
  selectedId: string | null;
  onPick: (id: string, name: string) => void;
}

/** Lightweight Drive folder browser for choosing where to copy. Self-contained
 *  (its own navigation state) so it doesn't touch ImportToDriveModal. Starts
 *  from the last-used destination (shared localStorage key) or My Drive root. */
function DestFolderPicker({ token, selectedId, onPick }: DestFolderPickerProps) {
  const [stack, setStack] = useState<DriveFolder[]>([]);
  const [folders, setFolders] = useState<DriveFolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      let start: DriveFolder = { id: "root", name: "My Drive" };
      const savedId = localStorage.getItem(LAST_DEST_KEY);
      if (savedId) {
        try {
          start = await getFolder(savedId, token);
        } catch {
          /* fall back to root */
        }
      }
      try {
        const subs = await listSubfolders(start.id, token);
        if (cancelled) return;
        setStack([start]);
        setFolders(subs);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "Lỗi tải thư mục");
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const enter = useCallback(
    async (folder: DriveFolder) => {
      setLoading(true);
      setErr(null);
      try {
        const subs = await listSubfolders(folder.id, token);
        setStack((s) => [...s, folder]);
        setFolders(subs);
      } catch (e) {
        setErr(e instanceof Error ? e.message : "Lỗi tải thư mục");
      }
      setLoading(false);
    },
    [token],
  );

  const goTo = useCallback(
    async (index: number) => {
      const target = stack[index];
      if (!target) return;
      setLoading(true);
      setErr(null);
      try {
        const subs = await listSubfolders(target.id, token);
        setStack((s) => s.slice(0, index + 1));
        setFolders(subs);
      } catch (e) {
        setErr(e instanceof Error ? e.message : "Lỗi tải thư mục");
      }
      setLoading(false);
    },
    [stack, token],
  );

  /**
   * Go up one level. When we have in-session breadcrumb history, just pop to
   * the parent crumb. But the picker starts deep at the last-used folder
   * (single crumb), so there we resolve the real parent from Drive — otherwise
   * the admin is stuck at the saved folder with no way above it.
   */
  const up = useCallback(async () => {
    if (stack.length > 1) {
      await goTo(stack.length - 2);
      return;
    }
    const cur = stack[0];
    if (!cur || cur.id === "root") return;
    setLoading(true);
    setErr(null);
    try {
      const info = await getFolderWithParentName(cur.id, token);
      if (!info.parentId) {
        // Already at Drive root — nothing above.
        setLoading(false);
        return;
      }
      const parent: DriveFolder = { id: info.parentId, name: info.parentName ?? "Thư mục mẹ" };
      const subs = await listSubfolders(parent.id, token);
      setStack([parent]);
      setFolders(subs);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Lỗi lên thư mục mẹ");
    }
    setLoading(false);
  }, [stack, token, goTo]);

  const current = stack[stack.length - 1];

  return (
    <div className="rounded-lg border border-white/10 bg-black/20 p-2.5 space-y-2">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 flex-wrap text-[11px] text-white/50">
        {current && current.id !== "root" && (
          <button
            onClick={() => void up()}
            disabled={loading}
            className="flex-shrink-0 text-white/60 hover:text-white transition-colors disabled:opacity-40"
          >
            ‹ Lên thư mục mẹ
          </button>
        )}
        {stack.map((f, i) => (
          <span key={f.id} className="flex items-center gap-1">
            {i > 0 && <span className="text-white/25">/</span>}
            <button
              onClick={() => void goTo(i)}
              className="hover:text-white/85 transition-colors max-w-[140px] truncate"
              title={f.name}
            >
              {f.name}
            </button>
          </span>
        ))}
      </div>

      {/* Current folder + pick button */}
      {current && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-white/70 truncate" title={current.name}>
            📁 {current.name}
          </span>
          <button
            onClick={() => {
              localStorage.setItem(LAST_DEST_KEY, current.id);
              onPick(current.id, current.name);
            }}
            className={`text-[11px] px-2.5 py-1 rounded-md transition-colors flex-shrink-0 ${
              selectedId === current.id
                ? "bg-emerald-500/25 text-emerald-200"
                : "bg-[#B76E6E]/80 hover:bg-[#B76E6E] text-white"
            }`}
          >
            {selectedId === current.id ? "✓ Đã chọn" : "Chọn folder này"}
          </button>
        </div>
      )}

      {/* Subfolder list */}
      <div className="max-h-40 overflow-y-auto space-y-0.5">
        {loading ? (
          <p className="text-[11px] text-white/40 py-2 text-center">Đang tải…</p>
        ) : err ? (
          <p className="text-[11px] text-red-300/80 py-2">{err}</p>
        ) : folders.length === 0 ? (
          <p className="text-[11px] text-white/30 py-2 text-center">
            (Không có folder con)
          </p>
        ) : (
          folders.map((f) => (
            <button
              key={f.id}
              onClick={() => void enter(f)}
              className="w-full text-left text-xs text-white/70 hover:text-white hover:bg-white/5 px-2 py-1.5 rounded-md transition-colors truncate"
              title={f.name}
            >
              📁 {f.name}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
